// Step 1.5: Email Verification (After Account Creation)
// Security: Verify email before payment to ensure valid accounts
// UX: Clear instructions with resend functionality

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface EmailVerificationStepProps {
  email?: string;
  onVerificationComplete: () => void;
}

const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
  email,
  onVerificationComplete
}) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [checking, setChecking] = useState(false);

  // Auto-check if user is verified when component loads and periodically
  useEffect(() => {
    const checkEmailVerification = async () => {
      try {
        setChecking(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user?.email_confirmed_at) {
          console.log('✅ Email verified automatically, proceeding to next step');
          onVerificationComplete();
          return;
        }
      } catch (error) {
        console.error('Error checking email verification:', error);
      } finally {
        setChecking(false);
      }
    };

    // Check immediately
    checkEmailVerification();

    // Check periodically every 5 seconds
    const interval = setInterval(checkEmailVerification, 5000);

    return () => clearInterval(interval);
  }, [onVerificationComplete]);

  const handleResendVerification = async () => {
    if (!email) return;
    
    setResending(true);
    try {
      console.log('📧 Resending verification email to:', email);
      
      // Actually resend the verification email using Supabase
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/signup?step=email_verification&email=${encodeURIComponent(email)}`
        }
      });

      if (error) {
        console.error('❌ Failed to resend verification email:', error);
        throw error;
      }
      
      console.log('✅ Verification email resent successfully');
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (error: any) {
      console.error('❌ Failed to resend verification email:', error);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-md mx-auto text-center">
        
        {/* Email Icon */}
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        {/* Heading */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Check Your Email
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          We've sent a verification link to {email ? <strong>{email}</strong> : 'your email address'}
        </p>

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4">
            Next Steps:
          </h3>
          <div className="text-blue-800 dark:text-blue-200 text-sm space-y-3 text-left">
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-3 mt-1 font-bold">1.</span>
              <span>Open the email from EightyThreeLabs</span>
            </div>
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-3 mt-1 font-bold">2.</span>
              <span>Click the "Verify Email" button in the email</span>
            </div>
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-3 mt-1 font-bold">3.</span>
              <span>Return to this page to continue your signup</span>
            </div>
          </div>
        </div>

        {/* Resend Section */}
        <div className="space-y-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Didn't receive the email? Check your spam folder or
          </div>
          
          <button
            onClick={handleResendVerification}
            disabled={resending || resent}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              resent 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50'
            }`}
          >
            {resending ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : resent ? (
              '✅ Email Sent!'
            ) : (
              'Resend Verification Email'
            )}
          </button>
        </div>

        {/* Manual Check Button */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Already clicked the verification link?
          </div>
          <button
            onClick={async () => {
              setChecking(true);
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.user?.email_confirmed_at) {
                onVerificationComplete();
              } else {
                alert('Email not verified yet. Please check your email and click the verification link.');
              }
              setChecking(false);
            }}
            disabled={checking}
            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {checking ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking...
              </span>
            ) : (
              'Continue to Plan Selection'
            )}
          </button>
        </div>

        {/* Manual Continue Button (for testing) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Development Only:
            </div>
            <button
              onClick={onVerificationComplete}
              className="text-sm px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Skip Verification (Dev)
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 space-x-4">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">✓</div>
              <span>Account</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">2</div>
              <span className="font-medium text-blue-600 dark:text-blue-400">Verify Email</span>
            </div>
            <div className="flex items-center opacity-50">
              <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-medium mr-2">3</div>
              <span>Plan & Payment</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmailVerificationStep;